/**
 * 主题状态：全项目唯一的主题来源（light / dark / auto）。
 * 选择持久化在 localStorage；auto 时跟随系统外观，系统切换时即时更新。
 * 实际主题同步到 <html data-theme>，供 ECharts、CodeMirror 等非 antd 组件读取。
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type ThemeMode = 'light' | 'dark' | 'auto'
export type AppliedTheme = 'light' | 'dark'

const THEME_MODES: ThemeMode[] = ['light', 'dark', 'auto']

interface ThemeState {
  mode: ThemeMode
  systemPrefersDark: boolean
  setMode: (mode: ThemeMode) => void
  // 在 light、dark、auto 之间循环
  toggleMode: () => void
}

const darkMediaQuery = globalThis.matchMedia('(prefers-color-scheme: dark)')

function isThemeMode(value: unknown): value is ThemeMode {
  return THEME_MODES.includes(value as ThemeMode)
}

export function resolveTheme(mode: ThemeMode, systemPrefersDark: boolean): AppliedTheme {
  if (mode === 'auto') return systemPrefersDark ? 'dark' : 'light'
  return mode
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'auto',
      systemPrefersDark: darkMediaQuery.matches,
      setMode: (mode) => set({ mode }),
      toggleMode: () => {
        const nextIndex = (THEME_MODES.indexOf(get().mode) + 1) % THEME_MODES.length
        set({ mode: THEME_MODES[nextIndex] })
      }
    }),
    {
      name: 'acquilang-admin-theme',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ mode: state.mode }),
      // 存储里是非法值时回退为 auto
      merge: (persisted, current) => {
        const mode = (persisted as Partial<ThemeState> | undefined)?.mode
        return { ...current, mode: isThemeMode(mode) ? mode : 'auto' }
      }
    }
  )
)

// 当前实际主题（auto 已按系统外观解析）
export function useAppliedTheme(): AppliedTheme {
  return useThemeStore((state) => resolveTheme(state.mode, state.systemPrefersDark))
}

function syncDocumentTheme(state: ThemeState) {
  document.documentElement.dataset.theme = resolveTheme(state.mode, state.systemPrefersDark)
}

darkMediaQuery.addEventListener('change', (event) => {
  useThemeStore.setState({ systemPrefersDark: event.matches })
})

useThemeStore.subscribe(syncDocumentTheme)
syncDocumentTheme(useThemeStore.getState())
